/**
 * Utility function to generate consistent colors for staff members
 * Uses a hash function to assign colors based on staff member ID
 */

// Color palette - highly contrasting, distinct colors for better visibility
// Colors are chosen to maximize visual distinction - both backgrounds and borders are distinct
// Backgrounds are light tints, borders/dots are saturated for maximum contrast
// Colors are spaced to avoid conflicts (especially purple vs pink vs red)
const STAFF_COLORS = [
  { bg: '#bbdefb', border: '#1565c0', dot: '#1565c0' }, // Royal Blue - distinct
  { bg: '#ffcdd2', border: '#c62828', dot: '#c62828' }, // Crimson Red - distinct from pink
  { bg: '#c8e6c9', border: '#2e7d32', dot: '#2e7d32' }, // Forest Green - distinct
  { bg: '#ffe0b2', border: '#e65100', dot: '#e65100' }, // Deep Orange - distinct
  { bg: '#d1c4e9', border: '#512da8', dot: '#512da8' }, // Deep Indigo/Purple - clearly different from pink
  { bg: '#b2dfdb', border: '#00695c', dot: '#00695c' }, // Dark Teal - distinct from blue
  { bg: '#fff59d', border: '#f9a825', dot: '#f9a825' }, // Golden Yellow - distinct from orange
  { bg: '#90caf9', border: '#0277bd', dot: '#0277bd' }, // Sky Blue - different from royal blue
  { bg: '#f8bbd0', border: '#ad1457', dot: '#ad1457' }, // Rose Pink - clearly distinct from purple and red
  { bg: '#ffe082', border: '#ff8f00', dot: '#ff8f00' }, // Amber Orange - distinct from deep orange
  { bg: '#b39ddb', border: '#4527a0', dot: '#4527a0' }, // Medium Purple - different from deep indigo
  { bg: '#80deea', border: '#00838f', dot: '#00838f' }, // Turquoise Cyan - distinct from teal
  { bg: '#ffab91', border: '#d84315', dot: '#d84315' }, // Coral Red - distinct from crimson
  { bg: '#a5d6a7', border: '#1b5e20', dot: '#1b5e20' }, // Emerald Green - distinct from forest green
  { bg: '#9fa8da', border: '#3949ab', dot: '#3949ab' }, // Violet - distinct from other purples/blues
];

/**
 * Simple hash function to convert string to number
 */
function hashString(str) {
  let hash = 0;
  if (!str) return 0;
  for (let i = 0; i < str.length; i++) {
    const char = str.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash = hash & hash; // Convert to 32bit integer
  }
  return Math.abs(hash);
}

/**
 * Get color scheme for a staff member based on their ID
 * @param {string|Object} staffMember - Staff member object or ID string
 * @returns {Object} Color object with bg, border, and dot properties
 */
export function getStaffColor(staffMember) {
  let staffId;
  
  if (typeof staffMember === 'string') {
    staffId = staffMember;
  } else if (staffMember?._id) {
    staffId = staffMember._id.toString();
  } else if (staffMember?.id) {
    staffId = staffMember.id.toString();
  } else {
    // Fallback to a default color if ID can't be determined
    return STAFF_COLORS[0];
  }
  
  const hash = hashString(staffId);
  const colorIndex = hash % STAFF_COLORS.length;
  return STAFF_COLORS[colorIndex];
}

/**
 * Get background color for calendar events/shifts
 */
export function getStaffBackgroundColor(staffMember) {
  return getStaffColor(staffMember).bg;
}

/**
 * Get border color for calendar events/shifts
 */
export function getStaffBorderColor(staffMember) {
  return getStaffColor(staffMember).border;
}

/**
 * Get dot color for staff member indicators
 */
export function getStaffDotColor(staffMember) {
  return getStaffColor(staffMember).dot;
}

/**
 * Convert hex color to rgba with alpha transparency
 */
function hexToRgba(hex, alpha = 1) {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  if (!result) return hex;
  
  const r = parseInt(result[1], 16);
  const g = parseInt(result[2], 16);
  const b = parseInt(result[3], 16);
  
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

/**
 * Get background color with transparency for time off events
 */
export function getStaffBackgroundColorWithAlpha(staffMember, alpha = 0.5) {
  const bgColor = getStaffBackgroundColor(staffMember);
  return hexToRgba(bgColor, alpha);
}

